<template>
  <HrPageLayout
    title="Leave Report Dashboard"
    subtitle="Leave & Attendance report"
    icon="📋"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Leave & Attendance' },
      { label: 'Leave Report' }
    ]"
  >
    <template #tabs>
      <ReportTabs />
    </template>
    <div class="report-leave-content">
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-calendar-check"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Leave Days</p>
          <h2 class="hr-metric-value">{{ summary?.totalLeaveDays || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Approved Leaves</p>
          <h2 class="hr-metric-value">{{ summary?.approvedLeaves || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Pending Leaves</p>
          <h2 class="hr-metric-value">{{ summary?.pendingLeaves || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-x-circle"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Rejected Leaves</p>
          <h2 class="hr-metric-value">{{ summary?.rejectedLeaves || 0 }}</h2>
            </div>
        </div>
      </div>
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-plus-medical"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Sick Leave</p>
          <h2 class="hr-metric-value">{{ summary?.sickLeave || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-sun"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Annual Leave</p>
          <h2 class="hr-metric-value">{{ summary?.annualLeave || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-heart"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Parental Leave</p>
          <h2 class="hr-metric-value">{{ summary?.parentalLeave || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-book"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Other Leave</p>
          <h2 class="hr-metric-value">{{ summary?.otherLeave || 0 }}</h2>
            </div>
        </div>
      </div>

      <div class="hr-chart-card hr-mb-3">
        <div class="hr-chart-body hr-py-2">
          <div class="hr-flex hr-flex-wrap hr-align-center hr-gap-2">
            <strong>Leave Status:</strong>
            <span class="hr-badge hr-badge-success">Approved</span>
            <span class="hr-badge hr-badge-warning">Pending</span>
            <span class="hr-badge hr-badge-danger">Rejected</span>
            <span class="hr-badge hr-badge-info">Cancelled</span>
            <span class="hr-badge hr-badge-secondary">Expired</span>
          </div>
        </div>
      </div>

      <div class="hr-chart-card">
        <div class="hr-chart-header hr-flex-between hr-flex-wrap">
          <h5 class="hr-chart-title">Leave Report</h5>
          <div class="hr-flex hr-gap-2 hr-flex-wrap">
            <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="exportToExcelAdvanced"><i class="bx bx-file-blank hr-me-1"></i>Excel Advanced</button>
            <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="exportToPDF"><i class="bx bx-file hr-me-1"></i>PDF</button>
            <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-secondary" @click="exportToCSV"><i class="bx bx-copy hr-me-1"></i>CSV</button>
            <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-secondary" @click="printReport"><i class="bx bx-printer hr-me-1"></i>Print</button>
          </div>
        </div>

        <div class="hr-chart-body">
          <div class="hr-form-grid hr-mb-4">
            <div class="hr-filter-group">
              <label class="hr-form-label">Department</label>
              <select v-model="selectedDepartment" class="hr-select" @change="getLeaveReport">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.departmentName }}</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Period</label>
              <select v-model="selectedPeriod" class="hr-select" @change="getLeaveReport">
                <option value="current_month">Current Month</option>
                <option value="last_month">Last Month</option>
                <option value="last_3_months">Last 3 Months</option>
                <option value="year_to_date">Year to Date</option>
                <option value="custom">Custom Date Range</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Search Employee</label>
              <div class="hr-search-wrap">
                <input type="text" v-model="searchValue" class="hr-input" placeholder="Search by name...">
                <i class="bx bx-search-alt search-icon"></i>
              </div>
            </div>
          </div>
          <div class="hr-form-grid hr-mb-4" v-if="selectedPeriod === 'custom'">
            <div class="hr-filter-group">
              <label class="hr-form-label">Start Date</label>
              <input type="date" v-model="startDate" class="hr-input" @change="getLeaveReport">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">End Date</label>
              <input type="date" v-model="endDate" class="hr-input" @change="getLeaveReport">
            </div>
          </div>

          <div v-if="loading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p>Loading leave report data...</p>
          </div>
          <div v-else-if="filteredLeaveRecords.length === 0" class="hr-empty-state">
            <div class="hr-empty-icon"><i class="bx bx-calendar"></i></div>
            <h5>No leave records found</h5>
            <p class="hr-text-muted">Try adjusting your search or filters</p>
            <button @click="resetFilters" class="hr-btn hr-btn-sm hr-btn-outline-primary hr-mt-2">Reset Filters</button>
          </div>
          <div v-else class="hr-table-wrapper">
                    <table class="hr-table hr-table-bordered hr-table-hover text-nowrap">
                      <thead>
                      <tr>
                        <th scope="col">Employee</th>
                        <th scope="col">Department</th>
                        <th scope="col">Leave Type</th>
                        <th scope="col">Start Date</th>
                        <th scope="col">End Date</th>
                        <th scope="col">Duration</th>
                        <th scope="col">Status</th>
                        <th scope="col">Applied On</th>
                        <th scope="col">Approved By</th>
                        <th scope="col">Reason</th>
                      </tr>
                      </thead>
                      <tbody>
                      <!-- Leave record rows with status-based styling -->
                      <tr v-for="record in paginatedLeaveRecords" :key="record.id" class="leave-record-row">
                        <td>
                          <div class="hr-flex hr-align-center hr-gap-2">
                            <span v-if="record.avatar" class="hr-avatar-sm"><img :src="record.avatar" alt=""></span>
                            <span v-else class="hr-avatar-initials">{{ getInitials(record.employeeName) }}</span>
                            <span>{{ record.employeeName }}</span>
                          </div>
                        </td>
                        <td>{{ record.department }}</td>
                        <td>
                          <span :class="getLeaveTypeClass(record.leaveType)">{{ record.leaveType }}</span>
                        </td>
                        <td>{{ formatDate(record.startDate) }}</td>
                        <td>{{ formatDate(record.endDate) }}</td>
                        <td>{{ record.duration }} day(s)</td>
                        <td>
                          <span :class="getStatusBadgeClass(record.status)">{{ record.status }}</span>
                        </td>
                        <td>{{ formatDate(record.appliedOn) }}</td>
                        <td>{{ record.approvedBy || '—' }}</td>
                        <td>
                          <span v-if="record.reason && record.reason.length > 30">
                            {{ record.reason.substring(0, 30) }}...
                            <i class="bx bx-info-circle hr-text-info" v-tooltip="record.reason"></i>
                          </span>
                          <span v-else>{{ record.reason || '—' }}</span>
                        </td>
                      </tr>
                      </tbody>
                    </table>
          </div>

          <div class="hr-pagination-wrap hr-flex-between hr-mt-4">
            <div class="hr-flex hr-align-center hr-gap-2 hr-flex-wrap">
              <span class="hr-text-muted hr-small">Showing <strong>{{ paginatedLeaveRecords.length }}</strong> of <strong>{{ filteredLeaveRecords.length }}</strong> records</span>
              <label class="hr-flex hr-align-center hr-gap-1">
                <span class="hr-small">Per page:</span>
                <select v-model.number="pageSize" class="hr-select hr-small" style="width: 70px;" @change="onPageSizeChange">
                  <option :value="5">5</option>
                  <option :value="10">10</option>
                  <option :value="20">20</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                </select>
              </label>
            </div>
            <nav v-if="totalPages > 1" class="hr-pagination">
              <button type="button" class="hr-page-btn" :disabled="currentPage <= 1" @click="changePage(currentPage - 1)"><i class="bx bx-chevron-left"></i></button>
              <template v-for="(page, idx) in paginationPages" :key="'page-'+idx">
                <button v-if="page !== '...'" type="button" class="hr-page-btn" :class="{ active: page === currentPage }" @click="changePage(page)">{{ page }}</button>
                <span v-else class="hr-page-ellipsis">...</span>
              </template>
              <button type="button" class="hr-page-btn" :disabled="currentPage >= totalPages" @click="changePage(currentPage + 1)"><i class="bx bx-chevron-right"></i></button>
            </nav>
          </div>
        </div>
      </div>

      <div class="hr-chart-card hr-mt-4">
        <div class="hr-chart-header"><h5 class="hr-chart-title">Leave Balance Summary</h5></div>
        <div class="hr-chart-body">
          <div class="hr-content-grid" style="grid-template-columns: repeat(2, 1fr);">
            <div class="leave-chart-placeholder"><p class="hr-text-muted">Visualization of leave balance by department would appear here</p></div>
            <div class="leave-chart-placeholder"><p class="hr-text-muted">Visualization of leave balance by type would appear here</p></div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>
<script>
/* eslint-disable */
import { authUrl, execute, getRight, toast, county_logo } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ReportTabs from "@/components/reports/ReportTabs.vue";
import { buildBrandedHeaderRows, generateBrandedPDF, getCompanyName } from "@/utils/reportExportUtils";
import * as XLSX from "xlsx";

export default {
  name: "LeaveReport",
  components: { HrPageLayout, ReportTabs },

  data() {
    return {
      // Initialize the summary object with default values
      summary: {
        totalLeaveDays: 0,
        approvedLeaves: 0,
        pendingLeaves: 0,
        rejectedLeaves: 0,
        sickLeave: 0,
        annualLeave: 0,
        parentalLeave: 0,
        otherLeave: 0
      },

      // UI state
      loading: false,

      // Filter options
      selectedDepartment: '',
      selectedPeriod: 'current_month',
      searchValue: '',
      startDate: '',
      endDate: '',

      // Pagination
      currentPage: 1,
      pageSize: 10,

      // Data sources
      departments: [],
      leaveRecords: [],

      // Month names for date formatting
      months: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ]
    };
  },

  computed: {
    filteredLeaveRecords() {
      if (!this.leaveRecords || this.leaveRecords.length === 0) {
        return [];
      }

      return this.leaveRecords.filter(record => {
        const matchesDepartment = !this.selectedDepartment ||
            String(record.departmentId) === String(this.selectedDepartment);

        const matchesSearch = !this.searchValue ||
            (record.employeeName && record.employeeName.toLowerCase().includes(this.searchValue.toLowerCase()));

        return matchesDepartment && matchesSearch;
      });
    },

    paginatedLeaveRecords() {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.filteredLeaveRecords.slice(startIndex, endIndex);
    },

    totalPages() {
      return Math.ceil(this.filteredLeaveRecords.length / this.pageSize);
    },

    paginationPages() {
      const pages = [];
      const maxVisiblePages = 5;

      if (this.totalPages <= maxVisiblePages) {
        // Show all pages if there are less than maxVisiblePages
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Always show first page
        pages.push(1);

        if (this.currentPage > 3) {
          pages.push('...');
        }

        // Pages around current page
        const startPage = Math.max(2, this.currentPage - 1);
        const endPage = Math.min(this.totalPages - 1, this.currentPage + 1);

        for (let i = startPage; i <= endPage; i++) {
          pages.push(i);
        }

        if (this.currentPage < this.totalPages - 2) {
          pages.push('...');
        }

        // Always show last page
        if (this.totalPages > 1) {
          pages.push(this.totalPages);
        }
      }

      return pages;
    },
    headerStats() {
      return [
        { icon: 'bx bx-calendar-check', label: 'Total Days', value: this.summary?.totalLeaveDays || 0 },
        { icon: 'bx bx-check-circle', label: 'Approved', value: this.summary?.approvedLeaves || 0 },
        { icon: 'bx bx-time-five', label: 'Pending', value: this.summary?.pendingLeaves || 0 },
        { icon: 'bx bx-x-circle', label: 'Rejected', value: this.summary?.rejectedLeaves || 0 }
      ];
    }
  },

  mounted() {
    this.initializeDates();
    this.fetchDepartments();
    this.getLeaveReport();
  },

  methods: {
    initializeDates() {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      this.startDate = this.formatDateForInput(startOfMonth);
      this.endDate = this.formatDateForInput(endOfMonth);
    },

    formatDateForInput(date) {
      return date.toISOString().split('T')[0];
    },

    fetchDepartments() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              if (res.data.data && Array.isArray(res.data.data.departments)) {
                this.departments = res.data.data.departments;
              } else {
                console.log("No departments data found or incorrect data format");
                this.departments = [];
              }
            } else {
              console.error("Error fetching departments:", res.data.message || "Unknown error");
              // Check if toast plugin exists before using it
              if (this.$toast) {
                this.$toast.error("Failed to load departments: " + (res.data.message || "Unknown error"));
              }
            }
            this.loading = false;
          })
          .catch((e) => {
            const errorMessage = e && e.message ? e.message : "Unknown error";
            console.error("API error when fetching departments:", errorMessage);
            // Check if toast plugin exists before using it
            if (this.$toast) {
              this.$toast.error("Network error: " + errorMessage);
            }
            this.loading = false;
          });
    },

    getLeaveReport() {
      this.loading = true;

      // Update date range based on selected period
      this.updateDateRangeFromPeriod();

      // Prepare request data
      const data = new FormData();
      data.append("function", "getLeaveRequests");
      data.append("startDate", this.startDate);
      data.append("endDate", this.endDate);

      // Add department filter if selected
      if (this.selectedDepartment) {
        data.append("departmentId", this.selectedDepartment);
      }

      // Make API call
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              if (res.data.data && Array.isArray(res.data.data.leaveRequests)) {
                // Transform API response to match our data structure
                this.leaveRecords = res.data.data.leaveRequests.map(item => ({
                  id: item.id,
                  employeeName: item.names,
                  departmentId: item.departmentId,
                  department: this.getDepartmentNameById(item.departmentId) || item.departmentName || item.currentDeptName || '-',
                  leaveType: item.leaveTypeName,
                  startDate: (item.startDate || '').split(' ')[0],
                  endDate: (item.endDate || '').split(' ')[0],
                  duration: parseFloat(item.totalDays) || 0,
                  status: item.status,
                  appliedOn: (item.createdAt || '').split(' ')[0],
                  approvedBy: item.approvedBy || null,
                  reason: item.reason || '-'
                }));

                // Update summary metrics if available
                if (res.data.data.metrics) {
                  this.summary = {
                    totalLeaveDays: this.calculateTotalLeaveDays(this.leaveRecords),
                    approvedLeaves: parseInt(res.data.data.metrics.approvedRequests) || 0,
                    pendingLeaves: parseInt(res.data.data.metrics.pendingRequests) || 0,
                    rejectedLeaves: parseInt(res.data.data.metrics.rejectedRequests) || 0,
                    // We'll calculate these in updateSummary() based on the leaveRecords
                    sickLeave: 0,
                    annualLeave: 0,
                    parentalLeave: 0,
                    otherLeave: 0
                  };
                }

                // Calculate leave type summary
                this.updateSummary();
              } else {
                console.log("No leave requests found or incorrect data format");
                this.leaveRecords = [];
                this.resetSummary();
              }
            } else {
              console.error("Error fetching leave requests:", res.data.message || "Unknown error");
              if (this.$toast) {
                this.$toast.error("Failed to load leave report: " + (res.data.message || "Unknown error"));
              }
              this.leaveRecords = [];
              this.resetSummary();
            }
            this.loading = false;
          })
          .catch((e) => {
            const errorMessage = e && e.message ? e.message : "Unknown error";
            console.error("API error when fetching leave requests:", errorMessage);
            if (this.$toast) {
              this.$toast.error("Network error: " + errorMessage);
            }
            this.leaveRecords = [];
            this.resetSummary();
            this.loading = false;
          });
    },

    // Helper function to get department name by ID
    getDepartmentNameById(departmentId) {
      if (!departmentId) return null;
      const department = this.departments.find(dept => dept.id === departmentId);
      return department ? department.departmentName : null;
    },

    // Helper function to calculate total leave days
    calculateTotalLeaveDays(records) {
      return records.reduce((total, record) => {
        return total + (parseFloat(record.duration) || 0);
      }, 0);
    },

    // Reset summary to default values
    resetSummary() {
      this.summary = {
        totalLeaveDays: 0,
        approvedLeaves: 0,
        pendingLeaves: 0,
        rejectedLeaves: 0,
        sickLeave: 0,
        annualLeave: 0,
        parentalLeave: 0,
        otherLeave: 0
      };
    },

    updateDateRangeFromPeriod() {
      const now = new Date();
      let startDate, endDate;

      switch(this.selectedPeriod) {
        case 'current_month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          break;

        case 'last_month':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          endDate = new Date(now.getFullYear(), now.getMonth(), 0);
          break;

        case 'last_3_months':
          startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
          endDate = new Date();
          break;

        case 'year_to_date':
          startDate = new Date(now.getFullYear(), 0, 1);
          endDate = new Date();
          break;

        case 'custom':
          // Use the custom date range as is
          return;
      }

      this.startDate = this.formatDateForInput(startDate);
      this.endDate = this.formatDateForInput(endDate);
    },

    updateSummary() {
      // Calculate leave type statistics from leave records
      let sickLeave = 0;
      let annualLeave = 0;
      let parentalLeave = 0;
      let otherLeave = 0;

      this.leaveRecords.forEach(record => {
        // Count by type
        if (record.leaveType === 'Sick Leave') sickLeave += record.duration;
        else if (record.leaveType === 'Annual Leave') annualLeave += record.duration;
        else if (record.leaveType === 'Parental Leave') parentalLeave += record.duration;
        else otherLeave += record.duration;
      });

      // Update leave type counts
      this.summary.sickLeave = sickLeave;
      this.summary.annualLeave = annualLeave;
      this.summary.parentalLeave = parentalLeave;
      this.summary.otherLeave = otherLeave;
    },

    resetFilters() {
      this.selectedDepartment = '';
      this.searchValue = '';
      this.selectedPeriod = 'current_month';
      this.updateDateRangeFromPeriod();
      this.getLeaveReport();
      this.currentPage = 1;
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    onPageSizeChange() {
      this.currentPage = 1; // Reset to first page when changing page size
    },

    formatDate(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return `${date.getDate()} ${this.months[date.getMonth()]} ${date.getFullYear()}`;
    },

    getInitials(name) {
      if (!name) return '';

      return name
          .split(' ')
          .map(part => part.charAt(0))
          .join('')
          .toUpperCase()
          .substring(0, 2);
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 'Approved': return 'hr-badge hr-badge-success';
        case 'Pending': return 'hr-badge hr-badge-warning';
        case 'Rejected': return 'hr-badge hr-badge-danger';
        case 'Cancelled': return 'hr-badge hr-badge-info';
        default: return 'hr-badge hr-badge-secondary';
      }
    },

    getLeaveTypeClass(type) {
      switch (type) {
        case 'Sick Leave': return 'hr-text-info';
        case 'Annual Leave': return 'hr-text-muted';
        case 'Parental Leave': return 'hr-text-muted';
        default: return 'hr-text-primary';
      }
    },

    buildExportRows() {
      const rows = this.filteredLeaveRecords;
      const headers = ['Employee', 'Department', 'Leave Type', 'Start Date', 'End Date', 'Duration (days)', 'Status', 'Applied On', 'Approved By', 'Reason'];
      const data = [headers];
      rows.forEach(record => {
        data.push([
          record.employeeName || '',
          record.department || '',
          record.leaveType || '',
          this.formatDate(record.startDate) || '',
          this.formatDate(record.endDate) || '',
          record.duration != null ? String(record.duration) : '',
          record.status || '',
          this.formatDate(record.appliedOn) || '',
          record.approvedBy || '—',
          (record.reason || '—').replace(/\r?\n/g, ' ')
        ]);
      });
      return { headers, data, rows };
    },

    exportToExcel() {
      const { data, rows } = this.buildExportRows();
      if (rows.length === 0) {
        toast.warning('No leave records to export');
        return;
      }
      try {
        const wb = XLSX.utils.book_new();
        const brandedRows = buildBrandedHeaderRows({
          companyName: getCompanyName(),
          reportTitle: 'Leave Report',
          period: `${this.startDate} to ${this.endDate}`
        });
        const fullData = [...brandedRows, ...data];
        const ws = XLSX.utils.aoa_to_sheet(fullData);
        const colWidths = [{ wch: 22 }, { wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 18 }, { wch: 40 }];
        ws['!cols'] = colWidths;
        XLSX.utils.book_append_sheet(wb, ws, 'Leave Report');
        const filename = `LeaveReport_${this.startDate}_to_${this.endDate}.xlsx`;
        XLSX.writeFile(wb, filename);
        toast.success('Excel report downloaded');
      } catch (e) {
        console.error(e);
        toast.error('Failed to export Excel');
      }
    },

    exportToExcelAdvanced() {
      const { data, rows } = this.buildExportRows();
      if (rows.length === 0) {
        toast.warning('No leave records to export');
        return;
      }
      try {
        const company = getCompanyName() || 'StaffSync';
        const period = `${this.startDate} to ${this.endDate}`;
        const logo = county_logo;

        // Build Excel XML/HTML structure with the logo at the top
        let html = `
          <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <!--[if gte mso 9]>
            <xml>
              <x:ExcelWorkbook>
                <x:ExcelWorksheets>
                  <x:ExcelWorksheet>
                    <x:Name>Leave Report</x:Name>
                    <x:WorksheetOptions>
                      <x:DisplayGridlines/>
                    </x:WorksheetOptions>
                  </x:ExcelWorksheet>
                </x:ExcelWorksheets>
              </x:ExcelWorkbook>
            </xml>
            <![endif]-->
            <style>
              .company-name { font-size: 16px; font-weight: bold; color: #1e293b; }
              .report-title { font-size: 14px; font-weight: bold; color: #475569; }
              .meta-text { font-size: 11px; color: #64748b; }
              th { background-color: #1e293b; color: #ffffff; font-weight: bold; text-align: left; }
              td, th { border: 0.5pt solid #cbd5e1; padding: 6px; font-family: sans-serif; font-size: 11px; }
              .summary-header { font-size: 12px; font-weight: bold; background-color: #f1f5f9; }
            </style>
          </head>
          <body>
            <table>
              <tr>
                <td colspan="10" height="70" align="left" valign="middle" style="border:none;">
                  <img src="${logo}" width="50" height="50">
                </td>
              </tr>
              ${company ? `<tr><td colspan="10" class="company-name" style="border:none;">${company}</td></tr>` : ''}
              <tr>
                <td colspan="10" class="report-title" style="border:none;">Leave Report Dashboard - Advanced Export</td>
              </tr>
              <tr>
                <td colspan="10" class="meta-text" style="border:none;">Period: ${period}</td>
              </tr>
              <tr>
                <td colspan="10" class="meta-text" style="border:none;">Generated: ${new Date().toLocaleString()}</td>
              </tr>
              <tr><td colspan="10" style="border:none;"></td></tr>

              <!-- Summary Section -->
              <tr class="summary-header">
                <td colspan="10">Leave Report Summary Statistics</td>
              </tr>
              <tr>
                <td colspan="2">Total Records</td>
                <td colspan="8">${rows.length}</td>
              </tr>
              <tr>
                <td colspan="2">Total Leave Days</td>
                <td colspan="8">${rows.reduce((s, r) => s + (parseFloat(r.duration) || 0), 0).toFixed(1)} days</td>
              </tr>
              <tr>
                <td colspan="2">Approved Leaves</td>
                <td colspan="8">${this.summary?.approvedLeaves || 0}</td>
              </tr>
              <tr>
                <td colspan="2">Pending Leaves</td>
                <td colspan="8">${this.summary?.pendingLeaves || 0}</td>
              </tr>
              <tr><td colspan="10" style="border:none;"></td></tr>

              <!-- Detailed Data -->
              <tr style="background-color: #1e293b; color: #ffffff;">
                ${data[0].map(h => `<th style="background-color: #1e293b; color: #ffffff; font-weight: bold;">${h}</th>`).join('')}
              </tr>
              ${data.slice(1).map(row => `
                <tr>
                  ${row.map(cell => `<td>${cell ?? ''}</td>`).join('')}
                </tr>
              `).join('')}
            </table>
          </body>
          </html>
        `;

        const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `LeaveReport_Advanced_${this.startDate}_to_${this.endDate}.xls`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success('Advanced Excel report downloaded');
      } catch (e) {
        console.error(e);
        toast.error('Failed to export Excel');
      }
    },

    exportToPDF() {
      if (this.filteredLeaveRecords.length === 0) {
        toast.warning('No leave records to export');
        return;
      }
      try {
        const columns = ['Employee', 'Department', 'Leave Type', 'Start', 'End', 'Duration', 'Status', 'Applied On', 'Approved By', 'Reason'];
        const rows = this.filteredLeaveRecords.map(r => [
          r.employeeName || '',
          r.department || '',
          r.leaveType || '',
          this.formatDate(r.startDate),
          this.formatDate(r.endDate),
          (r.duration || 0) + ' days',
          r.status || '',
          this.formatDate(r.appliedOn),
          r.approvedBy || '—',
          r.reason || '—'
        ]);

        generateBrandedPDF({
          reportTitle: 'Leave Report',
          period: `${this.startDate} to ${this.endDate}`,
          companyName: getCompanyName(),
          columns,
          rows,
          summary: {
            'Total Leave Days': this.summary?.totalLeaveDays || 0,
            'Approved': this.summary?.approvedLeaves || 0,
            'Pending': this.summary?.pendingLeaves || 0,
            'Rejected': this.summary?.rejectedLeaves || 0
          }
        });
      } catch (e) {
        toast.error('PDF generation failed');
      }
    },

    exportToCSV() {
      const { headers, data, rows } = this.buildExportRows();
      if (rows.length === 0) {
        toast.warning('No leave records to export');
        return;
      }
      try {
        const BOM = '\uFEFF';
        const csvContent = data.map(row => row.map(cell => {
          const s = String(cell == null ? '' : cell);
          const needsQuotes = /[",\r\n]/.test(s);
          return needsQuotes ? '"' + s.replace(/"/g, '""') + '"' : s;
        }).join(',')).join('\r\n');
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `LeaveReport_${this.startDate}_to_${this.endDate}.csv`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('CSV report downloaded');
      } catch (e) {
        console.error(e);
        toast.error('Failed to export CSV');
      }
    },

    printReport() {
      window.print();
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.report-leave-content { padding: 0; }
.hr-page-ellipsis { display: inline-flex; align-items: center; min-width: 36px; padding: 0 4px; color: #64748b; font-size: 14px; }
.search-icon { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: #74788d; }
.leave-chart-placeholder { padding: 1.5rem; background: #f8fafc; border-radius: 8px; }
.hr-avatar-sm { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; display: inline-block; }
.hr-avatar-initials { width: 28px; height: 28px; border-radius: 50%; background: #e2e8f0; color: #475569; display: inline-flex; align-items: center; justify-content: center; font-size: 0.7rem; font-weight: 600; }
</style>
